import React, { useState, useEffect } from "react"; // Import useEffect
import { Box, Button, useTheme } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useDivesitesContext } from "../hooks/useDivesitesContext";
import { useAuth } from "../contexts/AuthContext";
import auth from "../config/firebase";

// sections for this form
import BasicInfoSection from "./UpdateDivesite/BasicInfoSection";
import LocationDetailsSection from "./UpdateDivesite/LocationDetailsSection";
import CoordinatesSection from "./UpdateDivesite/CoordinatesSection";
import DescriptionSection from "./UpdateDivesite/DescriptionSection";
import DiveDetailsSection from "./UpdateDivesite/DiveDetailsSection";
import RatingsAndSlidersSection from "./UpdateDivesite/RatingsAndSlidersSection";

const ENVIRONMENTS = [
  { value: "Unknown", label: "Unknown" },
  { value: "Ocean-sea", label: "Ocean-sea" },
  { value: "Lake-quarry", label: "Lake-quarry" },
  { value: "Cave-cavern", label: "Cave-cavern" },
  { value: "Under-ice", label: "Under-ice" },
  { value: "Other", label: "Other" },
  { value: "undefined", label: "undefined" },
];

const PLATFORMS = [
  { value: "beach-shore", label: "beach-shore" },
  { value: "pier", label: "pier" },
  { value: "small-boat", label: "small-boat" },
  { value: "chater-boat", label: "charter-boat" },
  { value: "live-aboard", label: "live-aboard" },
  { value: "barge", label: "barge" },
  { value: "other", label: "other" },
];

const PROVINCES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District Of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const UpdateDivesiteForm = ({ divesite }) => {
  const { dispatch } = useDivesitesContext();
  const { userClaims } = useAuth();
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null); // Initialize formData to null
  const theme = useTheme(); // Use the theme from MUI
  // Update formData when the divesite prop changes
  useEffect(() => {
    if (divesite) {
      setFormData({
        name: divesite.uddf.divesite.site.name,
        longitude: divesite.uddf.divesite.site.geography.longitude,
        latitude: divesite.uddf.divesite.site.geography.latitude,
        country: divesite.uddf.divesite.site.geography.address.country,
        environment: divesite.uddf.divesite.site.environment,
        platform: divesite.uddf.dive.informationbeforedive.platform,
        location: divesite.uddf.divesite.site.geography.location,
        city: divesite.uddf.divesite.site.geography.address.city,
        province: divesite.uddf.divesite.site.geography.address.province,
        divesitePara: divesite.uddf.divesite.site.notes.para,
        diveDescription: divesite.uddf.divesite.site.shore.notes.para,
        oneliner: divesite.metadata.oneliner,
        depth: [
          divesite.uddf.divesite.site.sitedata.minimumdepth,
          divesite.uddf.divesite.site.sitedata.maximumdepth,
        ],
        surfaceSwimDistance:
          divesite.metadata.additionalInfo.surfaceSwimDistance,
        slogValue: divesite.metadata.ratings.slog,
      });
    } else {
      setFormData(null); // Reset form if no divesite is selected
    }
  }, [divesite]); // зависимость от prop divesite

  if (!userClaims || !userClaims.isAdmin) {
    return <div>You do not have permission to access this page.</div>;
  }

  // Render nothing if no divesite is selected yet
  if (!formData) {
    return null;
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const divesiteData = {
      uddf: {
        generator: { name: "Diveproject", version: 0 },
        divesite: {
          site: {
            name: formData.name,
            environment: formData.environment,
            geography: {
              longitude: formData.longitude,
              latitude: formData.latitude,
              location: formData.location,
              address: {
                city: formData.city,
                province: formData.province,
                country: formData.country,
              },
            },
            sitedata: {
              minimumdepth: formData.depth[0],
              maximumdepth: formData.depth[1],
            },
            shore: { notes: { para: formData.diveDescription } },
            notes: { para: formData.divesitePara },
          },
        },
        dive: {
          informationbeforedive: { platform: formData.platform },
        },
      },
      metadata: {
        oneliner: formData.oneliner,
        ratings: { slog: formData.slogValue },
        additionalInfo: { surfaceSwimDistance: formData.surfaceSwimDistance },
      },
    };

    try {
      const user = auth.currentUser;
      const token = user && (await user.getIdToken());

      const response = await fetch(
        `/api/divesites/${divesite._id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(divesiteData),
        }
      );

      const json = await response.json();
      if (response.ok) {
        setError(null);
        dispatch({ type: "UPDATE_DIVESITE", payload: json }); // Changed to UPDATE_DIVESITE
      } else {
        throw new Error(json.error || "Update failed");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    }
  };

  return (
    formData && ( // Only render the form if formData is not null
      <Box
        component="form"
        sx={{ "& > :not(style)": { m: 1, pr: 2 } }}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        sx={{
          '& .MuiInputLabel-root': {
            color: theme.palette.text.primary, // Changed to a standard theme text color
          }
        }}
        
      >
        <BasicInfoSection
          name={formData.name}
          location={formData.location}
          onChange={handleChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary, // Changed to a standard theme text color
            }
          }}
        />
        <LocationDetailsSection
          city={formData.city}
          province={formData.province}
          country={formData.country}
          provinces={PROVINCES}
          onChange={handleChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary, // Changed to a standard theme text color
            }
          }}
        />
        <CoordinatesSection
          latitude={formData.latitude}
          longitude={formData.longitude}
          onChange={handleChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary, // Changed to a standard theme text color
            }
          }}
        />
        <DescriptionSection
          divesitePara={formData.divesitePara}
          diveDescription={formData.diveDescription}
          oneliner={formData.oneliner}
          onChange={handleChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary, // Changed to a standard theme text color
            }
          }}
        />
        <DiveDetailsSection
          environment={formData.environment}
          platform={formData.platform}
          environments={ENVIRONMENTS}
          platforms={PLATFORMS}
          onChange={handleChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary, // Changed to a standard theme text color
            }
          }}
        />
        <RatingsAndSlidersSection
          depth={formData.depth}
          slogValue={formData.slogValue}
          surfaceSwimDistance={formData.surfaceSwimDistance}
          onChange={handleChange}
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary, // Changed to a standard theme text color
            }
          }}
        />
        <Button
          variant="contained"
          type="submit"
          startIcon={<SaveIcon />}
          sx={{ mt: 2 }}
        >
          Update divesite
        </Button>

        {error && <div className="error">{error}</div>}
      </Box>
    )
  );
};

export default UpdateDivesiteForm;